<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-75"
    menu-class="dropdown-menu-media dropdown-menu-right"
    left
  >
    <template #button-content>
      <feather-icon
        :badge="badgeCount"
        badge-classes="bg-danger"
        class="text-body"
        icon="VideoIcon"
        size="21"
        v-b-tooltip.hover.button.viewport="
          `${$t('assigned_video_tours.title')}`
        "
      />
    </template>

    <li class="dropdown-menu-header" @click.stop @mousedown.stop @mouseup.stop>
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          <router-link :to="{ name: routeName }">
            <feather-icon icon="LinkIcon" />
            <span class="align-middle ml-50">{{
              $t("assigned_video_tours.title")
            }}</span>
          </router-link>
        </h4>

        <div>
          <b-badge pill variant="light-primary">
            {{ badgeCount }} {{ $t("assigned_video_tours.video_tours") }}
          </b-badge>
        </div>
      </div>
    </li>

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <div class="px-1 py-1" @click.stop @mousedown.stop @mouseup.stop>
        <b-media class="mb-1">
          <template #aside>
            <b-avatar size="32">
              <span class="d-flex align-items-center">
                <feather-icon icon="UserIcon" size="16" />
              </span>
            </b-avatar>
          </template>

          <p class="media-heading mb-50">
            <span class="font-weight-bolder">
              {{ $t("assigned_video_tours.supervisor") }}:
              <small class="notification-text">{{ supervisorLabel }}</small>
            </span>
          </p>

          <div class="mt-50">
            <b-badge
              v-for="s in displayVideoTours"
              :key="s.id"
              pill
              variant="light-primary"
              class="mr-50 mb-50"
            >
              {{ s.name || s.description || `#${s.id}` }}
            </b-badge>
          </div>
        </b-media>

        <div v-if="isEditing" @click.stop @mousedown.stop @mouseup.stop>
          <label class="text-capitalize">{{
            $t("assigned_video_tours.supervisor")
          }}</label>
          <v-select
            v-model="selectedSupervisorId"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="users"
            :reduce="(val) => val.value"
            class="mb-1"
            append-to-body
          />

          <label class="text-capitalize">{{
            $t("assigned_video_tours.video_tours")
          }}</label>
          <v-select
            v-model="selectedVideoTourIds"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="videoTourOptions"
            multiple
            :reduce="(val) => val.value"
            class="mb-1"
            append-to-body
          />
        </div>
      </div>
    </vue-perfect-scrollbar>

    <li
      class="dropdown-menu-footer p-1"
      @click.stop
      @mousedown.stop
      @mouseup.stop
    >
      <div v-if="!isEditing">
        <b-button variant="primary" block @click.stop="startEditing">
          {{ $t("assigned_video_tours.change") }}
        </b-button>
      </div>

      <div v-else class="d-flex">
        <b-button
          variant="primary"
          block
          class="mr-50"
          :disabled="isSaving"
          @click.stop="saveChange"
        >
          {{ $t("actions.save") }}
        </b-button>
        <b-button
          variant="outline-secondary"
          block
          :disabled="isSaving"
          @click.stop="cancelEditing"
        >
          {{ $t("actions.cancel") }}
        </b-button>
      </div>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import { BNavItemDropdown, BBadge, BButton, BMedia, BAvatar } from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { computed, ref, onMounted, watch } from "@vue/composition-api";
import store from "@/store";
import vSelect from "vue-select";
import axios from "@/libs/axios";
import { decryptData } from "@/utils/decryptUserDataUtils";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BNavItemDropdown,
    VuePerfectScrollbar,
    BBadge,
    BButton,
    BMedia,
    BAvatar,
    vSelect,
  },
  props: {
    routeName: {
      type: String,
      default: "user_assigned_video_tours",
    },
  },
  setup() {
    const toast = useToast();
    const isEditing = ref(false);
    const isSaving = ref(false);

    const currentUser = JSON.parse(decryptData(window.localStorage.userData));
    const analystUserId = currentUser.id;
    const users = ref([]);

    const videoTours = ref([]);

    const getVideos = async () => {
        try {
            const { data } = await axios.get("v1/video-tours", { params: { per_page: 500 } });
            videoTours.value = data.data;
        } catch (error) {
            console.error(error);
        }
    };
    getVideos()
    const videoTourOptions = computed(() =>
        videoTours.value.map((tour) => ({
            value: tour.id,
            label: tour.name
        }))
    );

    const current = computed(() => store.getters["user_assigned_video_tour/getCurrent"]);
    const displayVideoTours = computed(() => current.value?.video_tours || []);
    const badgeCount = computed(() => displayVideoTours.value.length || 0);

    const selectedSupervisorId = ref(null);
    const selectedVideoTourIds = ref([]);

    function buildUserOptionLabel(user) {
      const parts = [user?.name, user?.user, user?.email].filter(Boolean);
      return parts.join(" | ");
    }

    const supervisorLabel = computed(() => {
      const sup = current.value?.supervisor;
      if (!sup) return "-";
      return buildUserOptionLabel(sup) || `#${sup.id}`;
    });

    const loadCurrent = () =>
      store.dispatch("user_assigned_video_tour/getCurrent", {
        analyst_user_id: analystUserId,
      });

    const syncFormFromCurrent = () => {
      selectedSupervisorId.value =
        current.value?.supervisor_user_id || current.value?.supervisor?.id || null;

      selectedVideoTourIds.value = Array.isArray(current.value?.video_tours)
        ? current.value.video_tours.map((s) => s.id)
        : [];
    };

    const startEditing = () => {
      syncFormFromCurrent();
      isEditing.value = true;
    };

    const cancelEditing = () => {
      syncFormFromCurrent();
      isEditing.value = false;
    };

    const saveChange = () => {
      if (!selectedSupervisorId.value || !selectedVideoTourIds.value.length) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Selecciona supervisor y video tours",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      isSaving.value = true;

      store
        .dispatch("user_assigned_video_tour/changeNow", {
          analyst_user_id: analystUserId,
          supervisor_user_id: selectedSupervisorId.value,
          video_tour_ids: selectedVideoTourIds.value,
        })
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response?.message || "Cambio realizado",
              icon: "CheckIcon",
              variant: "success",
            },
          });

          return loadCurrent();
        })
        .then(() => {
          syncFormFromCurrent();
          isEditing.value = false;
        })
        .catch((err) => {
          const msg = err?.response?.data?.message || "Error cambiando asignación";

          toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          isSaving.value = false;
        });
    };

    const getUsers = async () => {
      const params = {
        profiles: ['supervisor_vigilancia', 'operador_vigilancia']
      }

      await axios.get('v1/user/by-profiles', { params })
        .then(response => {
          const { data } = response;

          users.value = data.data.map((user) => ({
            value: user.id,
            label: buildUserOptionLabel(user),
          }));
        })
        .catch(() => {
          users.value = [];
        });
    }

    onMounted(async () => {
      await getUsers();

      loadCurrent().then(() => {
        syncFormFromCurrent();
      });
    });

    watch(current, () => {
      if (!isEditing.value) syncFormFromCurrent();
    });

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    return {
      perfectScrollbarSettings,
      users,
      videoTourOptions,
      displayVideoTours,
      badgeCount,
      supervisorLabel,
      selectedSupervisorId,
      selectedVideoTourIds,
      isEditing,
      isSaving,
      startEditing,
      cancelEditing,
      saveChange
    };
  },
};
</script>
